<template>
  <!-- Desktop: Rail sidebar -->
  <!-- Widened by the notch of an iPhone in landscape, which its padding keeps off the icons -->
  <v-navigation-drawer v-if="!mobile" rail permanent class="app-navigation-rail" :rail-width="68 + safeAreaLeft">
    <div class="d-flex flex-column align-center pa-2">
      <!-- Platform Logo -->
      <router-link :to="{ name: 'home' }" class="logo-container">
        <div class="logo-wrapper">
          <!-- A white-label brand's own logo (`GET /api/app/init`), else Videas'. -->
          <img
            v-if="session.platformLogoUrl"
            :src="session.platformLogoUrl"
            :alt="platformName"
            class="platform-logo-img"
          >
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 300 300"
            class="platform-logo-svg"
          >
            <rect width="300" height="300" rx="60" ry="60" fill="#884aff" />
            <path d="M67.52,72.26h37.44l38.87,119.58h.65l37.96-119.58h35.5l-55.71,155.47h-38.87l-55.84-155.47Z" fill="#fff" />
            <rect x="200.8" y="187.2" width="31.67" height="40.54" fill="#fff" />
          </svg>
        </div>
        <div v-if="!isVideasPlatform && platformName" class="org-name" v-overflow-tooltip>{{ platformName }}</div>
      </router-link>

      <v-divider class="my-3" width="40" thickness="1" />

      <!-- Navigation Items -->
      <v-tooltip
        v-for="app in menuApps"
        :key="app.id"
        location="end"
        :open-delay="500"
      >
        <template #activator="{ props: activator }">
          <router-link :to="{ name: app.route }" class="nav-item-link" v-bind="activator">
            <div class="nav-item-container">
              <div :class="{ 'icon-active': isActiveApp(app) }" class="nav-btn">
                <span v-html="app.icon" class="custom-icon" />
              </div>
              <div class="app-title">{{ $t(app.titleKey) }}</div>
            </div>
          </router-link>
        </template>
        <span class="text-caption">{{ $t(app.titleKey) }}</span>
      </v-tooltip>
    </div>

    <!-- Bottom section -->
    <template #append>
      <div class="d-flex flex-column align-center app-navigation-rail__bottom">
        <v-divider class="mb-4" width="32" />

        <!-- Extra bottom actions (e.g. help button) -->
        <slot name="bottom-extra" />

        <!-- User -->
        <v-tooltip location="end" :open-delay="500">
          <template #activator="{ props: activator }">
            <div v-bind="activator" class="user-btn" @click="handleUserClick">
              <Avatar
                v-if="session.user?.email"
                :email="session.user.email"
                :name="userName"
                :size="36"
              />
              <v-avatar v-else :size="36" color="grey-lighten-1">
                <v-icon :icon="mdiAccount" size="small" color="white" />
              </v-avatar>
            </div>
          </template>
          <span class="text-caption">{{ $t('core.navigation.profile') }}</span>
        </v-tooltip>
      </div>
    </template>
  </v-navigation-drawer>

  <!-- Phone: the bottom bar, four app slots then Plus (docs/plans/mobile-navigation.md MN3),
       given way to by a modal creation flow (MN9) -->
  <v-bottom-navigation
    v-else-if="!creationFlow"
    tag="nav"
    :aria-label="t('core.navigation.main')"
    :model-value="activeNavValue"
    class="mobile-bottom-nav"
    grow
  >
    <v-btn
      v-for="app in phoneApps"
      :key="app.id"
      :value="app.id"
      :to="{ name: app.route }"
      :aria-current="isActiveApp(app) ? 'page' : undefined"
    >
      <span v-html="app.icon" class="mobile-nav-icon" />
      <span class="mobile-nav-label">{{ t(app.titleKey) }}</span>
    </v-btn>

    <!-- Plus: the overflow apps, transfers, notifications, credits and the account, in a sheet.
         Active on an overflow app's route; its badge, the transfers to follow, is in its name. -->
    <v-btn
      value="more"
      data-more-sheet-opener
      aria-haspopup="dialog"
      :aria-expanded="layoutStore.userDrawerOpen"
      :aria-current="overflowActive ? 'page' : undefined"
      :aria-label="moreLabel"
      @click="layoutStore.openUserDrawer()"
    >
      <v-badge
        :content="activeTransfers"
        :model-value="activeTransfers > 0"
        color="primary"
        offset-x="-4"
        offset-y="-2"
      >
        <v-icon :icon="mdiDotsHorizontal" size="22" class="mobile-nav-icon" />
      </v-badge>
      <span class="mobile-nav-label">{{ t('core.navigation.more') }}</span>
    </v-btn>
  </v-bottom-navigation>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { mdiAccount, mdiDotsHorizontal } from '@mdi/js'
import { useSessionStore } from '@/apps/session/stores/session'
import { useLayoutStore } from '../../stores'
import { useMenuApps } from '../../composables/useMenuApps'
import { useCreationFlow } from '../../composables/useCreationFlow'
import { useSafeAreaInsets } from '../../composables/useSafeAreaInsets'
import Avatar from '../user/Avatar.vue'

const props = withDefaults(defineProps<{
  /**
   * The transfers to follow (running, or ended and waiting on the user), which Plus shows and
   * announces on a phone.
   */
  activeTransfers?: number
}>(), {
  activeTransfers: 0,
})

const { t } = useI18n()
const { mobile } = useDisplay()
const session = useSessionStore()
const layoutStore = useLayoutStore()
const { menuApps, phoneApps, overflowActive, isActiveApp } = useMenuApps()
const creationFlow = useCreationFlow()
const { left: safeAreaLeft } = useSafeAreaInsets()

// The bar's height leaves `--app-bottom-inset` with it, so what floats comes down to the edge.
watch(creationFlow, (hidden) => document.documentElement.classList.toggle('app-tabbar-hidden', hidden), {
  immediate: true,
})
onBeforeUnmount(() => document.documentElement.classList.remove('app-tabbar-hidden'))

const platformName = computed(() => session.init?.platform.name ?? '')
const platformSlug = computed(() => session.init?.platform.slug ?? '')
const isVideasPlatform = computed(() => !platformSlug.value || platformSlug.value === 'videas')
const userName = computed(() => session.user?.name || session.user?.email || '')

const activeNavValue = computed(() => {
  if (overflowActive.value) return 'more'
  return phoneApps.value.find(isActiveApp)?.id
})

const moreLabel = computed(() =>
  props.activeTransfers > 0
    ? t('core.navigation.moreWithTransfers', { count: props.activeTransfers }, props.activeTransfers)
    : t('core.navigation.more'),
)

function handleUserClick() {
  layoutStore.openUserDrawer()
}
</script>

<style lang="scss">
@use '@/styles/design-tokens' as *;

// =============================================================================
// Desktop Rail
// =============================================================================
.app-navigation-rail {
  padding-left: env(safe-area-inset-left, 0px);
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-surface), 0.95);
  backdrop-filter: blur(10px);

  // Hide scrollbar globally
  &, * {
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // The bottom stack grows with what the shell adds to it (notifications, credits, transfers),
  // and on a full-height window — Safari in fullscreen has no toolbar below the page — 8px left
  // the avatar against the screen's edge. The safe area is the browser's own inset, when it has one.
  &__bottom {
    padding: 8px 8px calc(16px + env(safe-area-inset-bottom, 0px));
  }

  .logo-container {
    position: relative;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    text-decoration: none;
    color: inherit;

    .logo-wrapper {
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .platform-logo-img,
    .platform-logo-svg {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      object-fit: contain;
      transition: transform 0.15s ease;
    }

    &:hover .platform-logo-img,
    &:hover .platform-logo-svg {
      transform: scale(1.05);
    }

    .org-name {
      font-size: 0.65rem;
      font-weight: 600;
      color: rgba(var(--v-theme-on-surface), 0.7);
      margin-top: 0.25rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 60px;
      line-height: 1;
    }
  }

  .nav-item-link {
    text-decoration: none;
    display: block;
    margin-bottom: 1rem;

    &:hover .nav-item-container {
      .nav-btn:not(.icon-active) .custom-icon {
        color: rgba(var(--v-theme-on-surface), 0.87);
      }

      .app-title {
        color: rgba(var(--v-theme-on-surface), 0.95);
      }
    }
  }

  .nav-item-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    cursor: pointer;
  }

  .nav-btn {
    transition: all 0.2s ease;
    position: relative;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(var(--v-theme-surface), 1);

    &.icon-active {
      background-color: rgba(var(--v-theme-primary), 0.08);

      .custom-icon {
        color: rgb(var(--v-theme-primary));
      }
    }
  }

  .custom-icon {
    color: rgba(var(--v-theme-on-surface), 0.6);
    display: inline-flex;
    transition: color 0.2s ease;
    width: 22px;
    height: 22px;
  }

  .app-title {
    letter-spacing: 0.02em;
    font-size: 0.625rem;
    color: rgba(var(--v-theme-on-surface), 0.7);
    margin-top: 0.25rem;
    font-weight: 500;
    transition: color 0.2s ease;
  }

  .user-btn {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
}

// =============================================================================
// Mobile Bottom Navigation
// =============================================================================
.mobile-bottom-nav {
  // The iPhone's gesture bar is the bar's too, as `--app-bottom-inset` counts it (MN4): its
  // background runs under it and its buttons keep their 56 px above it. Vuetify sizes the bar, and
  // tells `v-main`, from `height` alone.
  height: calc(#{$tabbar-h} + env(safe-area-inset-bottom, 0px)) !important;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  background: rgba(var(--v-theme-surface), 0.97) !important;
  backdrop-filter: blur(10px);

  .v-btn {
    text-transform: none !important;
    letter-spacing: $tracking-normal !important;
    min-width: 0 !important;
    opacity: $opacity-secondary;
    @include transition-fast;

    &--active {
      opacity: 1;

      .mobile-nav-icon {
        color: rgb(var(--v-theme-primary));
      }

      .mobile-nav-label {
        color: rgb(var(--v-theme-primary));
      }
    }
  }

  .mobile-nav-icon {
    width: 22px;
    height: 22px;
    display: inline-flex;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    @include transition-fast(color);
  }

  .mobile-nav-label {
    font-size: $text-xs;
    font-weight: $font-medium;
    letter-spacing: $tracking-wide;
    margin-top: 2px;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    @include transition-fast(color);
  }
}
</style>
